import type { ReactElement } from "react";
import { Link } from "react-router";

import type { FieldErrors } from "../../api/client.js";
import { describedBy, errorIdOf } from "../../forms/described-by.js";
import { FormSummary } from "../../forms/fields.js";
import { useSearchForm } from "../../forms/use-search-form.js";

import type { EstimateConditions, EstimatePlanChoice } from "./estimate-types.js";
import { DateField, errorOf, text } from "./payment-search-form.js";
import { adminCenterUrls } from "./urls.js";

/** 欄の下に出す断り（番号・期間）。ほか（プランなど）は先頭にまとめて出す。 */
const fieldNames = new Set(["number", "from", "to"]);

const summaryOf = (errors: FieldErrors): string[] => Object.entries(errors)
    .filter(([key]) => !fieldNames.has(key.toLowerCase()))
    .flatMap(([, messages]) => messages);

/** 見積番号の欄（断りを欄の下に出す）。 */
function NumberField({ value, error }: { readonly value: string; readonly error: string | undefined }): ReactElement {
    const invalid = error !== undefined;
    return (
        <div>
            <label htmlFor="number" className="form-label">見積番号</label>
            <input
                id="number" name="number" className={invalid ? "form-input is-invalid" : "form-input"} autoComplete="off"
                defaultValue={value} placeholder="Q261012-000123"
                aria-invalid={invalid ? true : undefined}
                {...describedBy("number", { help: false, invalid, required: false })}
            />
            {invalid ? <span className="form-error field-validation-error" id={errorIdOf("number")}>{error}</span> : null}
        </div>
    );
}

/**
 * 見積を探す窓。<b>どの検索とも同じ決まり</b>（<c>useSearchForm</c>。docs/01 §8）——
 * Enter で探してもフォーカスとスクロールを保つ・探し直すと 1 ページ目（ページはクエリに載せない）。
 * 番号と期間の断りは欄の下、ほかは先頭。プランの選択肢はサーバが返す。
 */
export function EstimateSearchForm({ conditions, plans, errors }: {
    readonly conditions: EstimateConditions;
    readonly plans: readonly EstimatePlanChoice[];
    readonly errors: FieldErrors;
}): ReactElement {
    const form = useSearchForm(adminCenterUrls.estimates(), {
        number: text(conditions.number), addressee: text(conditions.addressee),
        from: text(conditions.from), to: text(conditions.to), plan: text(conditions.plan),
    });

    return (
        <section className="card mb-4 p-6">
            <form method="get" {...form.props}>
                <FormSummary messages={summaryOf(errors)} />
                <div className="mb-3">
                    <label htmlFor="addressee" className="form-label">宛名で探す</label>
                    <input
                        id="addressee" name="addressee" className="form-input" autoComplete="off"
                        defaultValue={text(conditions.addressee)} placeholder="会社名などの一部"
                    />
                </div>

                <div className="flex flex-wrap items-end gap-3">
                    <NumberField value={text(conditions.number)} error={errorOf(errors, "number")} />
                    <DateField name="from" label="発行日（この日から）" value={text(conditions.from)} error={errorOf(errors, "from")} />
                    <DateField name="to" label="発行日（この日まで）" value={text(conditions.to)} error={errorOf(errors, "to")} />
                    <div>
                        <label htmlFor="plan" className="form-label">プラン</label>
                        <select id="plan" name="plan" className="form-input" defaultValue={text(conditions.plan)}>
                            <option value="">すべて</option>
                            {plans.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    {/* 送るボタンとクリアはひとまとまりにする（折り返すときも一緒に次の行へ落ちる。片方だけ別の行に分かれない） */}
                    <div className="flex gap-3">
                        <button type="submit" className="btn-primary">検索</button>
                        {/* **クリアは「条件の無い URL を開く」。**type="reset" は描いたときの値に戻るだけ */}
                        <Link to={adminCenterUrls.estimates()} className="btn-secondary">クリア</Link>
                    </div>
                </div>
                <p className="form-help">
                    番号は全部か、番号の末尾の数字だけで探せます。<strong className="text-ink-800">期間を入れなければ全期間</strong>から、新しい順に並べます。
                </p>
            </form>
        </section>
    );
}
